'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Navigation from '../../../components/Navigation';
import RelatedPages from '../../../components/RelatedPages';
import { GuideEyebrow, GuideSection, GuideSubtitle, GuideTitle } from '../../../components/GuideHeading';

const Code = ({ children }: { children: string }) => (
    <Box
        component="pre"
        sx={{
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            p: 2,
            mb: 3,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            lineHeight: 1.6,
        }}
    >
        {children}
    </Box>
);

export default function JsonPatchInGoGuide() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" className="main-container">
                    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                        <GuideEyebrow>Guide</GuideEyebrow>
                        <GuideTitle>JSON Patch in Go</GuideTitle>
                        <GuideSubtitle>
                            How to apply and generate RFC 6902 JSON Patches in Go with the evanphx/json-patch library - the same implementation Kubernetes uses - with working code you can copy straight into a project.
                        </GuideSubtitle>

                        <GuideSection>Installing the library</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            The de-facto standard library is <code>evanphx/json-patch</code>. It implements every RFC 6902 operation - add, remove, replace, move, copy and test:
                        </Typography>
                        <Code>{`go get github.com/evanphx/json-patch/v5`}</Code>

                        <GuideSection>Applying a JSON Patch</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            <code>jsonpatch.DecodePatch</code> parses a patch document, and <code>patch.Apply</code> executes it against a JSON document. Both work on raw <code>[]byte</code>:
                        </Typography>
                        <Code>{`package main

import (
    "fmt"

    jsonpatch "github.com/evanphx/json-patch/v5"
)

func main() {
    doc := []byte(\`{"name": "demo", "version": 1, "tags": ["a", "b"]}\`)

    patchJSON := []byte(\`[
        {"op": "replace", "path": "/version", "value": 2},
        {"op": "add", "path": "/tags/-", "value": "c"},
        {"op": "remove", "path": "/name"}
    ]\`)

    patch, err := jsonpatch.DecodePatch(patchJSON)
    if err != nil {
        panic(err) // the patch document itself is malformed
    }

    patched, err := patch.Apply(doc)
    if err != nil {
        panic(err) // an operation failed against this document
    }

    fmt.Println(string(patched))
    // {"tags":["a","b","c"],"version":2}
}`}</Code>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            Note the special path <code>/tags/-</code>: the dash appends to the end of an array. Paths follow JSON Pointer (RFC 6901), so <code>~</code> and <code>/</code> inside keys are escaped as <code>~0</code> and <code>~1</code>.
                        </Typography>

                        <GuideSection>Generating a patch from two documents</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            <code>jsonpatch.CreatePatch</code> diffs two documents and returns the RFC 6902 operations that turn the first into the second - the same thing the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link> does in your browser:
                        </Typography>
                        <Code>{`original := []byte(\`{"name": "demo", "version": 1}\`)
modified := []byte(\`{"name": "demo", "version": 2, "tags": ["a"]}\`)

patchJSON, err := jsonpatch.CreatePatch(original, modified)
if err != nil {
    panic(err)
}

fmt.Println(string(patchJSON))
// [{"op":"replace","path":"/version","value":2},{"op":"add","path":"/tags","value":["a"]}]`}</Code>

                        <GuideSection>Handling test failures and errors</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Two different things can go wrong, and they deserve different handling. <code>DecodePatch</code> fails when the patch itself is invalid JSON or uses an unknown operation. <code>Apply</code> fails when a well-formed patch does not fit the document - a missing path, an array index out of range, or a <code>test</code> operation whose value does not match:
                        </Typography>
                        <Code>{`patch, err := jsonpatch.DecodePatch(patchJSON)
if err != nil {
    // malformed patch: reject it at the API boundary
    return fmt.Errorf("invalid patch: %w", err)
}

patched, err := patch.Apply(doc)
if err != nil {
    // e.g. a test operation failed: the document is not
    // in the state the caller expected - HTTP 412 territory
    return fmt.Errorf("patch rejected: %w", err)
}`}</Code>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            RFC 6902 stops evaluation at the first failed operation. Whether earlier mutations are rolled back depends on the library, so do not assume transactional behavior without testing the implementation.
                        </Typography>

                        <GuideSection>Common pitfalls</GuideSection>
                        <Typography component="ul" sx={{ color: 'var(--fg-muted)', mb: 3, pl: 3 }}>
                            <li><strong>Evaluation stops on error.</strong> RFC 6902 does not require rollback of earlier operations; verify the library&apos;s behavior before relying on transaction-like semantics. Applying a patch twice is not idempotent for <code>add</code> on arrays.</li>
                            <li><strong>Numbers are all float64.</strong> <code>encoding/json</code> unmarshals numbers to <code>float64</code>, so a test on <code>{'{"value": 1}'}</code> compares against <code>1.0</code>. The library normalises this, but mixing <code>json.Number</code> and floats in your own comparisons bites.</li>
                            <li><strong>Array indices shift.</strong> Removing index 0 makes index 1 become index 0. Order remove operations from the highest index down, or use value-based test guards.</li>
                            <li><strong>Apply does not mutate the input.</strong> It returns a new <code>[]byte</code>; assign the result.</li>
                        </Typography>

                        <GuideSection>Try it in the browser first</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Before writing Go, prototype the patch visually. Paste your two documents into the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link> to get the exact RFC 6902 operations, or use the{' '}
                            <Link href="/diff" style={{ color: 'var(--accent)' }}>JSON Diff tool</Link> to see every changed line. Then drop the generated patch into <code>patch.Apply</code>.
                        </Typography>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 4 }}>
                            New to the format? Read the <Link href="/docs" style={{ color: 'var(--accent)' }}>JSON Patch documentation</Link> or browse{' '}
                            <Link href="/examples" style={{ color: 'var(--accent)' }}>real-world examples</Link>.
                        </Typography>

                        <RelatedPages currentPage="docs" />
                    </Box>
                </Container>
            </main>
        </>
    );
}
